import logger from "lib/log";
import { put, select } from "redux-saga/effects";
import { aggregateID } from "lib/id";

import {
  doesPaymentExist,
  doesClubBookingExist,
  doesDinnerBookingExist,
  doesShopItemExist,
  doesStudentExist,
  doesParentalConsentItemExist
} from "state/school_money/selectors";
import {
  goToPayment,
  goToClubBooking,
  goToDinnerBooking,
  goToShopItem,
  goToParentalConsentItem
} from "state/school_money/actions";
import { openInformationModal } from "state/app/actions";
import { history } from "state/history";

const MISSING_PAYMENT_MESSAGE = "The payment you are looking for no longer exists.";
const MISSING_DINNER_MESSAGE = "The dinner item you are looking for no longer exists.";
const MISSING_CLUB_MESSAGE = "The club you are looking for no longer exists.";
const MISSING_CONSENT_MESSAGE = "The consent item you are looking for no longer exists.";
const MISSING_STUDENT_MESSAGE = "The student you are looking for no longer exist or the identity of the student has changed. Please contact our support team if you can't find the student you are looking for.";

/**
 * Go to payment
 */
export function* payment({ serverID, payload}) {
  const paymentID = aggregateID(serverID, payload.installmentID);

  if (yield select(doesPaymentExist, paymentID)) {
    yield put(goToPayment(paymentID));
  } else {
    history.push(`/school_money/payments/`);
    yield put(openInformationModal({ title : "Alert", message : MISSING_PAYMENT_MESSAGE }));
    logger.warn(`Process Metadata: school_money: invalid paymentID: ${paymentID}`);
  }

}

/**
 * Go to club booking
 */
export function* clubBooking({ serverID, payload}) {
  const clubBookingID = aggregateID(serverID, payload.installmentID);

  if (yield select(doesClubBookingExist, clubBookingID)) {
    yield put(goToClubBooking(clubBookingID));
  } else {
    history.push(`/school_money/clubs/`);
    yield put(openInformationModal({ title : "Alert", message : MISSING_CLUB_MESSAGE }));
    logger.warn(`Process Metadata: school_money: invalid clubBookingID: ${clubBookingID}`);
  }

}

/**
 * Go to dinner booking
 */
export function* dinnerBooking({ serverID, payload }) {
  // If there is no installmentID in the payload then it is a chase for topup
  if (payload.installmentID === undefined) {
    const studentID = aggregateID(serverID, payload.studentID);
    if (yield select(doesStudentExist, studentID)) {
      history.push(`/school_money/dinners/${studentID}/`);
    } else {
      yield put(openInformationModal({ title : "Alert", message : MISSING_STUDENT_MESSAGE }));
    }
    return;
  }

  const dinnerBookingID = aggregateID(serverID, payload.installmentID);

  if (yield select(doesDinnerBookingExist, dinnerBookingID)) {
    yield put(goToDinnerBooking(dinnerBookingID));
  } else {
    history.push(`/school_money/dinners/`);
    yield put(openInformationModal({ title : "Alert", message : MISSING_DINNER_MESSAGE }));
    logger.warn(`Process Metadata: school_money: invalid dinnerBookingID: ${dinnerBookingID}`);
  }

}

/**
 * Go to shop item
 */
export function* shopItem({ serverID, payload }) {
  const shopItemID = aggregateID(serverID, payload.shopItemID);
  const studentID = aggregateID(serverID, payload.studentID);

  const shopItemExists = yield select(doesShopItemExist, shopItemID);
  const studentExists = yield select(doesStudentExist, studentID);

  if (shopItemExists && studentExists) {
    yield put(goToShopItem(shopItemID, studentID));
  } else {
    if (!shopItemExists) {
      logger.warn(`Process Metadata: school_money: invalid shopItemID: ${shopItemID}`);
    }
    if (!studentExists) {
      logger.warn(`Process Metadata: school_money: invalid studentID: ${studentID}`);
    }
  }

}

/**
 * User has overdue payments - can be a mix of payment,
 *   club booking and dinner booking.
 * We just take them to the student's Payments page,
 *   since they can pay everything off there.
 */
export function* overdue({ serverID, payload }) {
  const studentID = aggregateID(serverID, payload.studentID);

  const studentExists = yield select(doesStudentExist, studentID);
  if (studentExists) {
    // no specific payment to go to, so just use history API to get to the page
    history.push(`/school_money/payments/${studentID}/`);
  } else {
    history.push(`/school_money/payments/`);
    yield put(openInformationModal({ title : "Alert", message : MISSING_STUDENT_MESSAGE }));
    logger.warn(`Process metadata: school_money: invalid studentID: ${studentID}`);
  }

}

/**
 * Go to parental consent
 */
export function* consent({ serverID, payload }) {
  const consentItemID = aggregateID(serverID, payload.installmentID);

  if (yield select(doesParentalConsentItemExist, consentItemID)) {
    yield put(goToParentalConsentItem(consentItemID));
  } else {
    history.push(`/school_money/parental_consent/`);
    yield put(openInformationModal({ title : "Alert", message : MISSING_CONSENT_MESSAGE }));
    logger.warn(`Process Metadata: school_money: invalid parentalConsentItemID: ${consentItemID}`);
  }

}



// WEBPACK FOOTER //
// ./src/state/school_money/sagas/processMetadataSaga/metadataSagas.js